import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { productsAPI } from '../services/productsAPI'
import productRolesAPI from '../services/productRolesAPI'
import { rolesAPI, plansAPI } from '../services/api'
import api from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import CreateRoleModal from '../components/Modals/CreateRoleModal'
import EditRoleModal from '../components/Modals/EditRoleModal'
import ConfirmationModal from '../components/Modals/ConfirmationModal'
import CreateConfigModal from '../components/Modals/CreateConfigModal'
import EditConfigModal from '../components/Modals/EditConfigModal'
import CreatePlanModal from '../components/Modals/CreatePlanModal'
import EditPlanModal from '../components/Modals/EditPlanModal'
import PlanCard from '../components/PlanCard'
import { ArrowLeft, Edit, Package, Settings, Users, CheckCircle, Plus, Trash2, ChevronDown, ChevronRight, Shield, FileText, ClipboardList, ExternalLink, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'

export default function ProductDetail() {
  const { hasPermission } = useAuth()
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [collapsedModules, setCollapsedModules] = useState(new Set())
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false)
  const [showEditRoleModal, setShowEditRoleModal] = useState(false)
  const [showDeleteRoleConfirmation, setShowDeleteRoleConfirmation] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)
  const [roleToDelete, setRoleToDelete] = useState(null)
  const [showCreateConfigModal, setShowCreateConfigModal] = useState(false)
  const [showEditConfigModal, setShowEditConfigModal] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [showDeleteConfigConfirmation, setShowDeleteConfigConfirmation] = useState(false)
  const [configToDelete, setConfigToDelete] = useState(null)
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false)
  const [showEditPlanModal, setShowEditPlanModal] = useState(false)
  const [showDeletePlanConfirmation, setShowDeletePlanConfirmation] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [planToDelete, setPlanToDelete] = useState(null)

  const { data, isLoading, refetch, error: productError } = useQuery(
    ['product', id], 
    () => productsAPI.getDetails(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnMount: false,
      onSuccess: (response) => {
        console.log('✅ Product Details API Response:', response)
        console.log('📦 Full Response Structure:', JSON.stringify(response, null, 2))
        const productData = response?.data?.data?.product
        if (productData) {
          console.log('📋 Product Data:', productData)
          console.log('🔧 Modules Source:', productData.modules_source)
          console.log('📊 Modules Count:', productData.modules?.length || 0)
          console.log('📝 Modules Data:', productData.modules)
          console.log('🎯 Features Count:', productData.features?.length || 0)
        } else {
          console.warn('⚠️ No product data found in response')
        }
      },
      onError: (error) => {
        console.error('❌ Error fetching product details:', error)
        console.error('❌ Error Response:', error.response)
        console.error('❌ Error Message:', error.message)
      }
    }
  )
  const product = data?.data?.data?.product || null

  // Fetch product roles - supports external API via /products/:id/roles endpoint - optimized caching
  const { data: rolesData, isLoading: rolesLoading, refetch: refetchRoles, error: rolesError } = useQuery(
    ['product-roles', id], 
    () => productsAPI.getRoles(id).then(r => r.data),
    { 
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onSuccess: (response) => {
        console.log('✅ Roles API Response:', response)
        console.log('📦 Roles Source:', response?.source || 'local')
        console.log('📊 Roles Count:', response?.data?.roles?.length || 0)
        console.log('📝 Roles Data:', response?.data?.roles)
        if (response?.warning) {
          console.warn('⚠️ Roles API Warning:', response.warning)
        }
      },
      onError: (error) => {
        console.error('❌ Error fetching roles:', error)
        console.error('❌ Error Response:', error.response)
      }
    }
  )
  // API returns: { success: true, data: { roles: [...] }, source: 'external' | 'local' }
  // After .then(r => r.data): rolesData = { success: true, data: { roles: [...] }, source: 'external' | 'local' }
  const roles = rolesData?.data?.roles || []
  const rolesSource = rolesData?.source || 'local'

  // Fetch users for this product - optimized caching
  const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useQuery(
    ['product-users', id],
    () => api.get(`/products/${id}/users`).then(r => r.data),
    { 
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        console.error('Error fetching users:', error)
      }
    }
  )
  const users = usersData?.data?.users || []

  // Fetch product config (for module fetching - gets the most recent one) - optimized caching
  const { data: configData, isLoading: configLoading, refetch: refetchConfig } = useQuery(
    ['product-config', id],
    () => productsAPI.getConfig(id),
    { 
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        // Only show error if it's not a 404 (config not found is expected)
        if (error.response?.status !== 404) {
          console.error('Error fetching config:', error)
        }
      }
    }
  )
  const config = configData?.data?.data?.config || null

  // Fetch all product configs (for Config tab display) - optimized caching
  const { data: configsData, isLoading: configsLoading, refetch: refetchConfigs } = useQuery(
    ['product-configs', id],
    () => productsAPI.getAllConfigs(id),
    { 
      enabled: !!id && activeTab === 'config',
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        // Only show error if it's not a 404 (configs not found is expected)
        if (error.response?.status !== 404) {
          console.error('Error fetching configs:', error)
        }
      }
    }
  )
  const configs = configsData?.data?.data?.configs || []

  // Fetch plans for this product - optimized caching
  const { data: plansData, isLoading: plansLoading, refetch: refetchPlans } = useQuery(
    ['plans', id],
    () => plansAPI.getByProduct(id).then(r => r.data),
    { 
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        // Only show error if it's not a 404 (plans not found is expected)
        if (error.response?.status !== 404) {
          console.error('Error fetching plans:', error)
        }
      }
    }
  )
  // Sort plans by display_order (ascending), with nulls last, then by created_at (descending) as fallback
  const plans = (plansData?.data?.plans || []).sort((a, b) => {
    const orderA = a.display_order ?? null
    const orderB = b.display_order ?? null
    
    // Handle null values - put them at the end
    if (orderA === null && orderB === null) {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0)
    }
    if (orderA === null) return 1 // a goes to end
    if (orderB === null) return -1 // b goes to end
    
    // Both have display_order, sort by it
    if (orderA !== orderB) {
      return orderA - orderB
    }
    
    // Same display_order, sort by created_at
    return new Date(b.created_at || 0) - new Date(a.created_at || 0)
  })

  // Helper functions to check config types
  const hasModuleConfig = configs.some(c => c.product_type === 'module')
  const hasRolesConfig = configs.some(c => c.product_type === 'roles')
  
  // Get config by type
  const getConfigByType = (type) => configs.find(c => c.product_type === type) || null
  const moduleConfig = getConfigByType('module')
  const rolesConfig = getConfigByType('roles')

  // Organize modules hierarchically
  const organizeModulesHierarchically = (modules) => {
    if (!modules || modules.length === 0) return []
    
    const moduleMap = new Map()
    const rootModules = []

    // First pass: create a map of all modules with children array
    modules.forEach(module => {
      moduleMap.set(module.id, { ...module, children: [] })
    })

    // Second pass: build the hierarchy
    modules.forEach(module => {
      // Check if module has a parent_id and that parent exists in our map
      if (module.parent_id && module.parent_id !== null && moduleMap.has(module.parent_id)) {
        moduleMap.get(module.parent_id).children.push(moduleMap.get(module.id))
      } else {
        rootModules.push(moduleMap.get(module.id))
      }
    })

    // Sort root modules by level and name
    rootModules.sort((a, b) => {
      if (a.level !== b.level) return a.level - b.level
      return a.name.localeCompare(b.name)
    })

    // Sort children within each module
    const sortChildren = (module) => {
      if (module.children && module.children.length > 0) {
        module.children.sort((a, b) => {
          if (a.level !== b.level) return a.level - b.level
          return a.name.localeCompare(b.name)
        })
        module.children.forEach(sortChildren)
      }
    }
    rootModules.forEach(sortChildren)

    return rootModules
  }

  // Debug logging - Enhanced
  useEffect(() => {
    console.log('='.repeat(60))
    console.log('🔍 PRODUCT DEBUG INFORMATION')
    console.log('='.repeat(60))
    
    if (product) {
      console.log('📦 Product Basic Info:', {
        id: product.id,
        name: product.name,
        modules_source: product.modules_source,
        modules_count: product.modules?.length || 0,
        features_count: product.features?.length || 0
      })
      
      console.log('📋 All Configs:', configs.map(c => ({
        type: c.product_type,
        http_method: c.http_method,
        endpoint: c.api_endpoint,
        id: c.id
      })))
      
      console.log('⚙️ Module Config (for fetching modules):', moduleConfig ? {
        type: moduleConfig.product_type,
        http_method: moduleConfig.http_method,
        endpoint: moduleConfig.api_endpoint
      } : '❌ NO MODULE CONFIG FOUND')
      
      console.log('📊 Modules Data:', product.modules)
      console.log('📊 Modules Array Length:', product.modules?.length || 0)
      
      if (product.modules_source === 'external') {
        console.log('🌐 ✅ External API Modules Detected')
        console.log('📡 External Modules Array:', product.modules)
        if (!product.modules || product.modules.length === 0) {
          console.warn('⚠️ ⚠️ ⚠️ WARNING: External API returned but modules array is EMPTY!')
          console.warn('💡 Possible reasons:')
          console.warn('   1. API response structure doesn\'t match expected format')
          console.warn('   2. API returned empty data')
          console.warn('   3. Response parsing failed - check backend logs')
        } else {
          console.log('✅ Modules are available:', product.modules.length, 'items')
          console.log('📋 First Module Sample:', JSON.stringify(product.modules[0], null, 2))
        }
      } else if (product.modules_source === 'local') {
        console.log('💾 Using Local Database Modules')
        console.log('📚 Local Modules:', product.modules)
        if (hasModuleConfig) {
          console.warn('⚠️ Module config exists but using local data - external API may have failed')
        }
      } else {
        console.log('📝 No modules_source specified - using default')
      }
      
      if (product.modules && product.modules.length > 0) {
        console.log('✅ ✅ ✅ Modules are available for display')
      } else {
        console.warn('⚠️ ⚠️ ⚠️ No modules to display')
        if (hasModuleConfig && moduleConfig) {
          console.warn('💡 You have a module config but no modules are showing.')
          console.warn('   Check backend console logs for API response details.')
          console.warn('   Config details:', {
            endpoint: moduleConfig.api_endpoint,
            method: moduleConfig.http_method,
            type: moduleConfig.product_type
          })
        }
      }
    } else {
      console.log('⏳ Product data not loaded yet or is null')
    }
    console.log('='.repeat(60))
  }, [product, config, configs, moduleConfig, hasModuleConfig])


  useEffect(() => {
    if (activeTab === 'roles' && id) {
      console.log('🔄 Roles tab activated, refetching roles...')
      refetchRoles()
    }
  }, [activeTab, id, refetchRoles])

  // Role management handlers
  const handleCreateRole = () => {
    setShowCreateRoleModal(true)
  }

  const handleEditRole = (role) => {
    setSelectedRole(role)
    setShowEditRoleModal(true)
  }

  const handleDeleteRole = (role) => {
    setRoleToDelete(role)
    setShowDeleteRoleConfirmation(true)
  }

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return

    try {
      await rolesAPI.delete(roleToDelete.id)
      toast.success('Role deleted successfully!')
      refetchRoles()
    } catch (error) {
      console.error('Error deleting role:', error)
      toast.error(error.message || 'Failed to delete role')
    } finally {
      setShowDeleteRoleConfirmation(false)
      setRoleToDelete(null)
    }
  }

  const handleRoleSuccess = () => {
    refetchRoles()
  }

  // Config management handlers
  const handleEditConfig = (config) => {
    setSelectedConfig(config)
    setShowEditConfigModal(true)
  }

  const handleDeleteConfig = (config) => {
    setConfigToDelete(config)
    setShowDeleteConfigConfirmation(true)
  }

  const confirmDeleteConfig = async () => {
    if (!configToDelete) return

    try {
      await productsAPI.deleteConfigById(id, configToDelete.id)
      toast.success('Config deleted successfully!')
      refetchConfigs() // Refresh the list of all configs
      refetchConfig() // Also refresh the single config for module fetching
    } catch (error) {
      console.error('Error deleting config:', error)
      toast.error(error.response?.data?.message || 'Failed to delete config')
    } finally {
      setShowDeleteConfigConfirmation(false)
      setConfigToDelete(null)
    }
  }

  const handleConfigSuccess = () => {
    refetchConfigs() // Refresh the list of all configs
    refetchConfig() // Also refresh the single config for module fetching
    refetch() // Refresh product details to fetch modules from external API if config type is 'module'
    refetchRoles()
  }

  // Plan management handlers
  const handleEditPlan = (plan) => {
    setSelectedPlan(plan)
    setShowEditPlanModal(true)
  }

  const handleDeletePlan = (plan) => {
    setPlanToDelete(plan)
    setShowDeletePlanConfirmation(true)
  }

  const confirmDeletePlan = async () => {
    if (!planToDelete) return

    try {
      await plansAPI.delete(planToDelete.id)
      toast.success('Plan deleted successfully!')
      refetchPlans()
    } catch (error) {
      console.error('Error deleting plan:', error)
      toast.error(error.response?.data?.message || 'Failed to delete plan')
    } finally {
      setShowDeletePlanConfirmation(false)
      setPlanToDelete(null)
    }
  }

  const handlePlanSuccess = () => {
    refetchPlans()
  }

  const getProductTypeLabel = (type) => {
    const labels = {
      module: 'Module',
      roles: 'Roles',
      feature: 'Feature'
    }
    return labels[type] || type
  }

  const toggleModuleCollapse = (moduleId) => {
    setCollapsedModules(prev => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }

  // Render module with its children recursively (read-only from product API)
  const renderModuleWithChildren = (module, depth = 0) => {
    const hasChildren = module.children && module.children.length > 0
    const isCollapsed = collapsedModules.has(module.id)
    const limitType = module.limit_type && module.limit_type !== 'none' ? module.limit_type : null

    return (
      <div key={module.id} className="space-y-2">
        <Card className={`${depth > 0 ? 'ml-6 border-l-2 border-l-blue-200' : ''}`}>
          <CardContent className="p-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1 flex-wrap gap-y-1">
                {hasChildren && (
                  <button
                    onClick={() => toggleModuleCollapse(module.id)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title={isCollapsed ? 'Expand sub-modules' : 'Collapse sub-modules'}
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                )}
                <h4 className="font-medium text-secondary-900">{module.name}</h4>
                {module.type === 'required' && (
                  <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">Required</span>
                )}
                {module.type === 'optional' && (
                  <span className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700">Optional</span>
                )}
                {limitType && (
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                    {limitType}{module.limit_unit ? ` · ${module.limit_unit}` : ''}
                  </span>
                )}
                {hasChildren && (
                  <span className="text-xs text-secondary-500">
                    {module.children.length} sub-module{module.children.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {module.description && (
                <p className="text-sm text-secondary-600">{module.description}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {hasChildren && !isCollapsed && (
          <div className="space-y-2">
            {module.children.map(child => renderModuleWithChildren(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-secondary-900 mb-4">Product Not Found</h2>
        <p className="text-secondary-600 mb-4">The product you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>
      </div>
    )
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      draft: { color: 'bg-yellow-100 text-yellow-800', label: 'Draft' }
    }
    
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }


  const tabs = [
    { id: 'overview', name: 'Overview', icon: Package },
    { id: 'modules', name: 'Modules', icon: Settings },
    { id: 'roles', name: 'Roles', icon: Shield },
    { id: 'users', name: 'Users', icon: Users },
    { id: 'plan', name: 'Plan', icon: ClipboardList },
    { id: 'config', name: 'Config', icon: FileText }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/products')}
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {product.image_url && (
            <div className="h-16 w-16 rounded-lg overflow-hidden bg-secondary-100 flex-shrink-0">
              <img 
                src={product.image_url} 
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-secondary-900">{product.name}</h1>
              {product.category && (
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                  {product.category}
                </span>
              )}
            </div>
            <p className="text-secondary-600 mt-1">{product.description || 'No description available'}</p>
            {product.url && (
              <a 
                href={product.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 mt-1"
              >
                <ExternalLink className="h-3 w-3" />
                {product.url}
              </a>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          {getStatusBadge(product.status)}
          {hasPermission('products.update') && (
            <Button onClick={() => navigate(`/products/${id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Product
            </Button>
          )}
        </div>
      </div>

      {/* Product Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                <Settings className="h-4 w-4 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-secondary-600">Modules</p>
                <p className="text-lg font-semibold text-secondary-900">
                  {product.modules ? product.modules.length : 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <ClipboardList className="h-4 w-4 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-secondary-600">Plans</p>
                <p className="text-lg font-semibold text-secondary-900">
                  {plans?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardHeader>
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              )
            })}
          </div>
        </CardHeader>

        <CardContent>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-3">Product Information</h3>
                {product.image_url && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">Product Image</label>
                    <div className="rounded-lg overflow-hidden bg-secondary-100 max-w-md">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-64 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Name</label>
                    <p className="text-sm text-secondary-900">{product.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Status</label>
                    <p className="text-sm">{getStatusBadge(product.status)}</p>
                  </div>
                  {product.category && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Category</label>
                      <p className="text-sm text-secondary-900">{product.category}</p>
                    </div>
                  )}
                  {product.url && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">URL</label>
                      <a 
                        href={product.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {product.url}
                      </a>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Description</label>
                    <p className="text-sm text-secondary-900">
                      {product.description || 'No description available'}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-3">Timeline</h3>
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    <span className="text-secondary-600">Created on {new Date(product.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    <span className="text-secondary-600">Last updated on {new Date(product.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'modules' && (
            <div className="space-y-4">
              {(() => {
                console.log('🎨 Rendering Modules Tab')
                console.log('📊 Current Product State:', {
                  hasProduct: !!product,
                  modulesSource: product?.modules_source,
                  modulesCount: product?.modules?.length || 0,
                  modules: product?.modules,
                  hasModuleConfig,
                  moduleConfig: moduleConfig
                })
                return null
              })()}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-secondary-900">Product Modules</h3>
                  {product?.modules_source === 'external' && hasModuleConfig && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Settings className="h-3 w-3 mr-1" />
                      External API
                    </span>
                  )}
                  {product?.modules_source === 'local' && hasModuleConfig && (
                    <span 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 cursor-help"
                      title="Click to view details"
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      Local (External API unavailable)
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => refetch()}
                    title="Refresh Modules"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {product?.modules_source === 'local' && hasModuleConfig && moduleConfig && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <Settings className="h-4 w-4 text-yellow-600 mt-0.5 mr-2" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-800">
                        External API Unavailable
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        The system attempted to fetch modules from the configured external API endpoint but encountered an error. 
                        Check your server logs for detailed error information, or verify:
                      </p>
                      <ul className="text-xs text-yellow-700 mt-2 ml-4 list-disc">
                        <li>API endpoint URL is correct: <code className="bg-yellow-100 px-1 rounded">{moduleConfig?.api_endpoint}</code></li>
                        <li>API key is valid and has proper permissions</li>
                        <li>External API server is accessible and responding</li>
                        <li>API response format matches expected structure</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {(() => {
                const hasModules = product.modules && product.modules.length > 0
                console.log('🔍 Modules Display Check:', {
                  hasModules,
                  modulesLength: product.modules?.length || 0,
                  modulesArray: product.modules,
                  organizedModules: hasModules ? organizeModulesHierarchically(product.modules) : []
                })
                return hasModules ? (
                  <div className="space-y-3" key={`modules-${product.modules.length}`}>
                    {organizeModulesHierarchically(product.modules).map(module => 
                      renderModuleWithChildren(module)
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Settings className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-secondary-900 mb-2">No modules yet</h3>
                    <p className="text-secondary-600 mb-4">
                      {product?.modules_source === 'external' 
                        ? 'External API did not return any modules' 
                        : 'Configure a module API in Config to load modules from your product'}
                    </p>
                    {product?.modules_source === 'external' && (
                      <p className="text-xs text-secondary-500 mt-2">
                        Check browser console and backend logs for API response details
                      </p>
                    )}
                  </div>
                )
              })()}
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="space-y-4">
              {(() => {
                console.log('🎨 Rendering Roles Tab')
                console.log('📊 Current Roles State:', {
                  hasRoles: roles.length > 0,
                  rolesCount: roles.length,
                  rolesSource: rolesSource,
                  hasRolesConfig,
                  rolesConfig: rolesConfig,
                  roles: roles
                })
                return null
              })()}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-secondary-900">Roles</h3>
                  {hasRolesConfig && rolesSource === 'external' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Settings className="h-3 w-3 mr-1" />
                      External API
                    </span>
                  )}
                  {hasRolesConfig && rolesSource === 'local' && rolesData?.warning && (
                    <span 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 cursor-help"
                      title={rolesData.warning}
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      Local (External API unavailable)
                    </span>
                  )}
                  {hasRolesConfig && rolesSource === 'local' && !rolesData?.warning && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      <Settings className="h-3 w-3 mr-1" />
                      Local Database
                    </span>
                  )}
                </div>
                {/* {!hasRolesConfig && (
                  <Button 
                    size="sm"
                    onClick={handleCreateRole}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Feature Role
                  </Button>
                )} */}
              </div>
              
              {rolesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : rolesError ? (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 text-red-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Feature Roles</h3>
                  <p className="text-red-600 mb-4">{rolesError.message || 'Failed to load feature roles'}</p>
                  <Button onClick={() => refetchRoles()}>
                    <Shield className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </div>
              ) : roles.length > 0 ? (
                <div className="space-y-3">
                  {(() => {
                    console.log('✅ Roles are available for display:', roles.length, 'items')
                    console.log('📋 First Role Sample:', roles[0])
                    return null
                  })()}
                  {roles.map((role) => (
                    <Card key={role.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-secondary-900 mb-1">{role.name}</h4>
                            <p className="text-sm text-secondary-600">{role.description || 'No description'}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditRole(role)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteRole(role)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">No roles yet</h3>
                  <p className="text-secondary-600 mb-4">
                    {hasRolesConfig && rolesSource === 'external' 
                      ? 'External API did not return any roles' 
                      : hasRolesConfig && rolesSource === 'local' && rolesData?.warning
                      ? `External API unavailable: ${rolesData.warning}`
                      : 'Create roles to define feature access permissions for this product'}
                  </p>
                  {hasRolesConfig && rolesSource === 'local' && rolesData?.warning && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-left max-w-md mx-auto">
                      <p className="text-sm font-medium text-yellow-800 mb-1">⚠️ External API Error:</p>
                      <p className="text-xs text-yellow-700 mb-1">{rolesData.warning}</p>
                      {rolesData?.error && (
                        <p className="text-xs text-yellow-600">{rolesData.error}</p>
                      )}
                      <p className="text-xs text-yellow-600 mt-2">
                        💡 Check backend console logs for detailed error information
                      </p>
                    </div>
                  )}
                  {hasRolesConfig && rolesSource === 'external' && (
                    <p className="text-xs text-secondary-500 mt-2">
                      Check browser console and backend logs for API response details
                    </p>
                  )}
                  {/* <Button onClick={handleCreateRole}>
                    <Shield className="h-4 w-4 mr-2" />
                    Add First Feature Role
                  </Button> */}
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">Users</h3>
              </div>
              
              {usersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : users.length > 0 ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <li key={user.id} className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <Users className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4 flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{user.full_name || user.username}</div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  user.status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.status || 'Inactive'}
                                </span>
                                <span className="text-xs text-gray-500">{user.role}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    <li className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <Users className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">No users found</div>
                          <div className="text-sm text-gray-500">This product doesn't have any assigned users yet.</div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'plan' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-secondary-900">Product Plans</h3>
                {hasPermission('products.create') && (
                  <Button 
                    size="sm"
                    onClick={() => setShowCreatePlanModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Plan
                  </Button>
                )}
              </div>

              {plansLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : plans.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {plans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      productModules={product?.modules || []}
                      onEdit={handleEditPlan}
                      onDelete={handleDeletePlan}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClipboardList className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">No plans yet</h3>
                  <p className="text-secondary-600 mb-4">Create plans to organize your product offerings</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'config' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-secondary-900">Product Configuration</h3>
                {hasPermission('products.create') && (
                  <Button 
                    size="sm"
                    onClick={() => setShowCreateConfigModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Config
                  </Button>
                )}
              </div>

              {configsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : configs && configs.length > 0 ? (
                <div className="bg-white shadow sm:rounded-md overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          API Endpoint
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          HTTP Method
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          API Key
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap sticky right-0 bg-gray-50 z-10">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {configs.map((config) => (
                        <tr key={config.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getProductTypeLabel(config.product_type)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 font-mono">{config.api_endpoint || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              config.http_method === 'GET' ? 'bg-green-100 text-green-800' :
                              config.http_method === 'POST' ? 'bg-blue-100 text-blue-800' :
                              config.http_method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                              config.http_method === 'DELETE' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {config.http_method || 'POST'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 font-mono">
                              {config.api_key ? `${config.api_key.substring(0, 10)}...` : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white">
                            <div className="flex items-center justify-end space-x-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleEditConfig(config)}
                                title="Edit Config"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDeleteConfig(config)}
                                className="text-red-600 hover:text-red-700"
                                title="Delete Config"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">No configuration yet</h3>
                  <p className="text-secondary-600 mb-4">Add a configuration to connect your product with external services</p>
                  <Button onClick={() => setShowCreateConfigModal(true)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Add First Config
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Role Modal */}
      <CreateRoleModal
        isOpen={showCreateRoleModal}
        onClose={() => setShowCreateRoleModal(false)}
        productId={id}
        onSuccess={handleRoleSuccess}
      />

      {/* Edit Role Modal */}
      <EditRoleModal
        isOpen={showEditRoleModal}
        onClose={() => {
          setShowEditRoleModal(false)
          setSelectedRole(null)
        }}
        role={selectedRole}
        productId={id}
        onSuccess={handleRoleSuccess}
      />

      {/* Delete Role Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteRoleConfirmation}
        onClose={() => {
          setShowDeleteRoleConfirmation(false)
          setRoleToDelete(null)
        }}
        onConfirm={confirmDeleteRole}
        title="Delete Feature Role"
        message={`Are you sure you want to delete the feature role "${roleToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Create Config Modal */}
      <CreateConfigModal
        isOpen={showCreateConfigModal}
        onClose={() => {
          setShowCreateConfigModal(false)
          handleConfigSuccess()
        }}
        productId={id}
      />

      {/* Edit Config Modal */}
      <EditConfigModal
        isOpen={showEditConfigModal}
        onClose={() => {
          setShowEditConfigModal(false)
          setSelectedConfig(null)
          handleConfigSuccess()
        }}
        productId={id}
        config={selectedConfig}
      />

      {/* Delete Config Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfigConfirmation}
        onClose={() => {
          setShowDeleteConfigConfirmation(false)
          setConfigToDelete(null)
        }}
        onConfirm={confirmDeleteConfig}
        title="Delete Configuration"
        message="Are you sure you want to delete this configuration? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Create Plan Modal */}
      <CreatePlanModal
        isOpen={showCreatePlanModal}
        onClose={() => setShowCreatePlanModal(false)}
        productId={id}
        modules={product?.modules || []}
        onSuccess={handlePlanSuccess}
      />

      {/* Edit Plan Modal */}
      <EditPlanModal
        isOpen={showEditPlanModal}
        onClose={() => {
          setShowEditPlanModal(false)
          setSelectedPlan(null)
          handlePlanSuccess()
        }}
        plan={selectedPlan}
        productId={id}
        modules={product?.modules || []}
        onSuccess={handlePlanSuccess}
      />

      {/* Delete Plan Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeletePlanConfirmation}
        onClose={() => {
          setShowDeletePlanConfirmation(false)
          setPlanToDelete(null)
        }}
        onConfirm={confirmDeletePlan}
        title="Delete Plan"
        message={`Are you sure you want to delete the plan "${planToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

    </div>
  )
}
